import type { InterviewCompileCandidate } from '@/services/campaigns/interview-compile-service'

const INTERNAL_KEYS = new Set([
    'objectId',
    'objectVersion',
    'ontologyVersion',
    'subjectId',
    'state',
    'provenance',
    'classification',
    'destinationProjectionPlan',
])

type ReviewShape =
    | 'Story'
    | 'Star point'
    | 'Fact'
    | 'Quote'
    | 'Beacon'
    | 'Ethos'
    | 'Identity reference'
    | 'Goal'
    | 'Audience'
    | 'ALO'
    | 'TFD'
    | 'Claim'
    | 'Proof reference'

type ReviewFieldSpec = {
    key: string
    label: string
    aliases?: string[]
    editable?: boolean
    required?: boolean
    multiline?: boolean
}

export type InterviewCompileReviewEntry = ReviewFieldSpec & {
    value: unknown
    source: 'root' | 'displayDraft' | 'domainPayload' | 'fallback'
}

export type InterviewCompileEditableField = ReviewFieldSpec & {
    value: string
    source: 'root' | 'displayDraft' | 'domainPayload' | 'new'
}

const FIELD_SPECS: Record<ReviewShape, ReviewFieldSpec[]> = {
    Story: [
        {
            key: 'canonicalStatement',
            label: 'Story statement',
            aliases: ['text'],
            editable: true,
            required: true,
            multiline: true,
        },
        { key: 'shortName', label: 'Story title', aliases: ['shortTitle', 'title', 'displayTitle'], editable: true },
        {
            key: 'episodeSummary',
            label: 'Episode summary',
            aliases: ['summary', 'description'],
            editable: true,
            multiline: true,
        },
        { key: 'maturity', label: 'Maturity' },
    ],
    'Star point': [
        { key: 'canonicalStatement', label: 'Star point statement', editable: true, required: true, multiline: true },
        { key: 'trait', label: 'Trait', aliases: ['label', 'shortLabel'], editable: true, required: true },
        {
            key: 'hook_line',
            label: 'Hook line',
            aliases: ['hookLine', 'statement'],
            editable: true,
            required: true,
            multiline: true,
        },
        {
            key: 'evidence',
            label: 'Evidence',
            aliases: ['sourceExcerpt', 'verbatim'],
            editable: true,
            required: true,
            multiline: true,
        },
        { key: 'tier', label: 'Tier' },
    ],
    Fact: [
        { key: 'canonicalStatement', label: 'Fact statement', editable: true, required: true, multiline: true },
        { key: 'title', label: 'Fact title', aliases: ['displayTitle'], editable: true },
        { key: 'assertionLine', label: 'Assertion', aliases: ['claim', 'statement'], editable: true, multiline: true },
        { key: 'factType', label: 'Fact type', aliases: ['category'] },
        { key: 'value', label: 'Value', editable: true, multiline: true },
    ],
    Quote: [
        { key: 'canonicalStatement', label: 'Quote', editable: true, required: true, multiline: true },
        {
            key: 'canonicalVerbatimText',
            label: 'Verbatim text',
            aliases: ['text', 'verbatim'],
            editable: true,
            multiline: true,
        },
        { key: 'sourceSpeakerRef', label: 'Speaker', aliases: ['speaker', 'attribution'] },
        { key: 'context', label: 'Context', aliases: ['contextNote', 'situation'], editable: true, multiline: true },
    ],
    Beacon: [
        { key: 'canonicalStatement', label: 'Beacon principle', editable: true, required: true, multiline: true },
        { key: 'principle', label: 'Principle', aliases: ['text', 'statement'], editable: true, multiline: true },
        { key: 'category', label: 'Category' },
    ],
    Ethos: [
        { key: 'canonicalStatement', label: 'Ethos statement', editable: true, required: true, multiline: true },
        { key: 'text', label: 'Ethos text', aliases: ['assertion', 'statement'], editable: true, multiline: true },
        { key: 'pillar', label: 'Pillar' },
    ],
    'Identity reference': [
        { key: 'canonicalStatement', label: 'Identity statement', editable: true, required: true, multiline: true },
        { key: 'axis', label: 'Identity axis', editable: true, required: true },
        { key: 'label', label: 'Identity label', aliases: ['major'], editable: true, required: true },
        { key: 'reflectionStatement', label: 'Reflection', aliases: ['sub'], editable: true, multiline: true },
        { key: 'identityFunction', label: 'Identity function' },
        { key: 'temporalStance', label: 'Temporal stance' },
        { key: 'roleAssertion', label: 'Role assertion' },
    ],
    Goal: [
        { key: 'canonicalStatement', label: 'Goal statement', editable: true, required: true, multiline: true },
        {
            key: 'goalStatement',
            label: 'Goal statement detail',
            aliases: ['intendedOutcome', 'statement'],
            editable: true,
            multiline: true,
        },
        { key: 'displayTitle', label: 'Goal title', aliases: ['shortName'], editable: true },
        { key: 'goalKind', label: 'Goal type', aliases: ['formationKind'] },
        { key: 'timeHorizon', label: 'Time horizon', editable: true },
        {
            key: 'successSignals',
            label: 'Success signals',
            aliases: ['successCriteria'],
            editable: true,
            multiline: true,
        },
    ],
    Audience: [
        { key: 'canonicalStatement', label: 'Audience description', editable: true, required: true, multiline: true },
        {
            key: 'shortTitle',
            label: 'Audience title',
            aliases: ['name', 'title', 'label', 'displayLabel'],
            editable: true,
            required: true,
        },
        {
            key: 'description',
            label: 'Audience description',
            aliases: ['summary', 'statement'],
            editable: true,
            required: true,
            multiline: true,
        },
        { key: 'role', label: 'Role', editable: true },
        { key: 'painPoints', label: 'Pain points', editable: true, multiline: true },
        { key: 'objections', label: 'Objections', editable: true, multiline: true },
        { key: 'targetKind', label: 'Target kind' },
    ],
    ALO: [
        { key: 'canonicalStatement', label: 'ALO assertion', editable: true, required: true, multiline: true },
        {
            key: 'assertion',
            label: 'Assertion',
            aliases: ['statement', 'assessmentStatement'],
            editable: true,
            multiline: true,
        },
        { key: 'kind', label: 'ALO kind', aliases: ['lane'] },
        { key: 'subtype', label: 'Subtype' },
        { key: 'goalRef', label: 'Goal reference' },
        { key: 'audienceRef', label: 'Audience reference' },
    ],
    TFD: [
        { key: 'canonicalStatement', label: 'Desired response', editable: true, required: true, multiline: true },
        { key: 'leg', label: 'TFD leg', aliases: ['primitiveKey'] },
        { key: 'audienceLabel', label: 'Audience', aliases: ['audience', 'audienceRef'] },
        { key: 'situationLabel', label: 'Situation', aliases: ['situation', 'situationRef'] },
        { key: 'desiredResponse', label: 'Desired response', editable: true, multiline: true },
        { key: 'currentResponse', label: 'Current response', editable: true, multiline: true },
        { key: 'goalRef', label: 'Goal reference' },
    ],
    Claim: [
        { key: 'canonicalStatement', label: 'Claim statement', editable: true, required: true, multiline: true },
        {
            key: 'claim',
            label: 'Claim detail',
            aliases: ['description', 'statement', 'text'],
            editable: true,
            multiline: true,
        },
        { key: 'claimType', label: 'Claim type' },
        { key: 'supportStatus', label: 'Support status' },
        { key: 'publicationBoundary', label: 'Publication boundary' },
        { key: 'proofRefs', label: 'Proof references' },
    ],
    'Proof reference': [
        { key: 'canonicalStatement', label: 'Proof description', editable: true, required: true, multiline: true },
        { key: 'description', label: 'Description', aliases: ['statement', 'text'], editable: true, multiline: true },
        { key: 'proofType', label: 'Proof type', aliases: ['kind', 'category'] },
        { key: 'achievedOutcome', label: 'Achieved outcome', aliases: ['outcome'], editable: true, multiline: true },
        { key: 'metric', label: 'Metric', aliases: ['measure', 'result'], editable: true },
        { key: 'evidenceRefs', label: 'Evidence references' },
    ],
}

function tokens(candidate: InterviewCompileCandidate): string {
    return `${candidate.objectType} ${(candidate.destinationSnapshot?.destinationKeys ?? []).join(' ')}`.toLowerCase()
}

export function interviewCompileReviewShapeLabel(candidate: InterviewCompileCandidate): ReviewShape | string {
    const objectType = candidate.objectType.toLowerCase()
    const value = tokens(candidate)
    const objectRules: Array<[string, ReviewShape]> = [
        ['star', 'Star point'],
        ['beacon', 'Beacon'],
        ['quote', 'Quote'],
        ['ethos', 'Ethos'],
        ['story', 'Story'],
        ['identity', 'Identity reference'],
        ['fact', 'Fact'],
        ['goal', 'Goal'],
        ['audience', 'Audience'],
        ['alo', 'ALO'],
        ['barrier', 'ALO'],
        ['tfd', 'TFD'],
        ['orientation', 'TFD'],
        ['claim', 'Claim'],
        ['proof', 'Proof reference'],
    ]
    const objectMatch = objectRules.find(([token]) => objectType.includes(token))
    if (objectMatch) return objectMatch[1]
    const destinationRules: Array<[string, ReviewShape]> = [
        ['starpoint', 'Star point'],
        ['star_points', 'Star point'],
        ['beacon', 'Beacon'],
        ['quote', 'Quote'],
        ['ethos', 'Ethos'],
        ['story', 'Story'],
        ['identity', 'Identity reference'],
        ['fact', 'Fact'],
        ['goal', 'Goal'],
        ['audience', 'Audience'],
        ['alo', 'ALO'],
        ['tfd', 'TFD'],
        ['orientation', 'TFD'],
        ['claim', 'Claim'],
        ['proof', 'Proof reference'],
    ]
    const destinationMatch = destinationRules.find(([token]) => value.includes(token))
    return destinationMatch?.[1] ?? candidate.objectType ?? 'Interview candidate'
}

function humanizeKey(key: string): string {
    return key
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, character => character.toUpperCase())
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function formatScalar(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Not provided'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    return String(value)
}

function indentLines(value: string, depth: number): string {
    const prefix = '  '.repeat(depth)
    return value
        .split('\n')
        .map(line => `${prefix}${line}`)
        .join('\n')
}

function formatReviewValue(value: unknown, depth = 0): string {
    if (!Array.isArray(value) && !isRecord(value)) return formatScalar(value)
    if (Array.isArray(value)) {
        if (!value.length) return 'None'
        return value
            .map(item =>
                isRecord(item)
                    ? `-\n${indentLines(formatReviewValue(item, depth + 1), depth + 1)}`
                    : `- ${formatReviewValue(item)}`
            )
            .join('\n')
    }
    const entries = Object.entries(value).filter(
        ([key, entry]) => !INTERNAL_KEYS.has(key) && entry !== undefined && entry !== null
    )
    if (!entries.length) return 'None'
    return entries
        .map(([key, entry]) => {
            const label = humanizeKey(key)
            if ((Array.isArray(entry) || isRecord(entry)) && !Object.keys(entry).length) return `${label}: None`
            if (Array.isArray(entry) || isRecord(entry))
                return `${label}:\n${indentLines(formatReviewValue(entry, depth + 1), depth + 1)}`
            return `${label}: ${formatScalar(entry)}`
        })
        .join('\n')
}

function sourceRecords(
    value: Record<string, unknown>
): Array<{ source: InterviewCompileReviewEntry['source']; record: Record<string, unknown> }> {
    const domainPayload = isRecord(value.domainPayload) ? value.domainPayload : {}
    const nestedRootRecords = Object.values(value).flatMap(entry => {
        if (Array.isArray(entry)) return entry.filter(isRecord)
        return isRecord(entry) ? [entry] : []
    })
    const nestedDomainPayloads = Object.values(domainPayload).filter(isRecord)
    return [
        { source: 'root', record: value },
        { source: 'displayDraft', record: isRecord(value.displayDraft) ? value.displayDraft : {} },
        { source: 'domainPayload', record: domainPayload },
        ...nestedRootRecords.map(record => ({ source: 'fallback' as const, record })),
        ...nestedDomainPayloads.map(record => ({ source: 'domainPayload' as const, record })),
    ]
}

function findField(value: Record<string, unknown>, spec: ReviewFieldSpec) {
    const aliases = [spec.key, ...(spec.aliases ?? [])]
    for (const { source, record } of sourceRecords(value)) {
        for (const key of aliases) {
            if (record[key] !== undefined && record[key] !== null && record[key] !== '')
                return { value: record[key], source }
        }
    }
    return null
}

function specsFor(candidate: InterviewCompileCandidate): ReviewFieldSpec[] {
    const shape = interviewCompileReviewShapeLabel(candidate)
    return shape in FIELD_SPECS ? FIELD_SPECS[shape as ReviewShape] : []
}

function fallbackEntries(value: Record<string, unknown>): InterviewCompileReviewEntry[] {
    return Object.entries(value)
        .filter(
            ([key, entry]) =>
                !INTERNAL_KEYS.has(key) &&
                !['canonicalStatement', 'domainPayload', 'displayDraft'].includes(key) &&
                entry !== undefined &&
                entry !== null &&
                entry !== ''
        )
        .map(([key, entry]) => ({ key, label: humanizeKey(key), value: entry, source: 'fallback' }))
}

export function interviewCompileReviewEntries(
    value: Record<string, unknown> | undefined,
    candidate?: InterviewCompileCandidate
): InterviewCompileReviewEntry[] {
    if (!value) return []
    const specs = candidate ? specsFor(candidate) : []
    const entries = specs
        .map(spec => {
            const found = findField(value, spec)
            // The proposed statement is already rendered as the primary harvest.
            // A nested current-profile record (for example { stories: [{ text }] })
            // still needs its statement shown in the comparison panel.
            if (!found || (spec.key === 'canonicalStatement' && found.source === 'root')) return null
            return { ...spec, value: found.value, source: found.source }
        })
        .filter(Boolean) as InterviewCompileReviewEntry[]
    // A known shape must not fall back to arbitrary root fields: those are
    // compiler metadata and would be rendered both as proposed content and
    // again in the metadata disclosure.
    return candidate ? entries : fallbackEntries(value)
}

export function interviewCompileReviewValue(value: unknown): string {
    return formatReviewValue(value)
}

function editableText(value: unknown): string {
    if (Array.isArray(value))
        return value.map(item => (isRecord(item) ? formatReviewValue(item) : String(item))).join('\n')
    if (isRecord(value)) return formatReviewValue(value)
    return value == null ? '' : String(value)
}

function editableSpecsFor(candidate: InterviewCompileCandidate): ReviewFieldSpec[] {
    return specsFor(candidate).filter(spec => spec.editable)
}

export function interviewCompileEditableFields(
    candidate: InterviewCompileCandidate,
    value: Record<string, unknown> | undefined
): InterviewCompileEditableField[] {
    if (!value) return []
    return editableSpecsFor(candidate)
        .map(spec => {
            const found = findField(value, spec)
            return {
                ...spec,
                value: found ? editableText(found.value) : '',
                source:
                    found?.source === 'root' || found?.source === 'displayDraft' || found?.source === 'domainPayload'
                        ? found.source
                        : 'new',
            }
        })
        .filter(field => field.required || field.value.trim()) as InterviewCompileEditableField[]
}

export function interviewCompileMissingRequiredFields(
    candidate: InterviewCompileCandidate,
    value: Record<string, unknown> | undefined
): string[] {
    return interviewCompileEditableFields(candidate, value)
        .filter(field => field.required && !field.value.trim())
        .map(field => field.label)
}

function parseEditedValue(original: unknown, text: string): unknown {
    if (Array.isArray(original))
        return text
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean)
    if (isRecord(original)) {
        const editableKey = Object.keys(original).find(key => typeof original[key] === 'string') ?? 'value'
        return { ...original, [editableKey]: text.trim() }
    }
    return text.trim()
}

function setField(value: Record<string, unknown>, spec: ReviewFieldSpec, text: string) {
    const next = JSON.parse(JSON.stringify(value)) as Record<string, unknown>
    const aliases = [spec.key, ...(spec.aliases ?? [])]
    const sources = sourceRecords(next)
    const existing = sources.find(({ record }) => aliases.some(key => record[key] !== undefined))
    const target =
        existing?.record ??
        (isRecord(next.domainPayload) ? next.domainPayload : (next.domainPayload = {} as Record<string, unknown>))
    const existingKey = aliases.find(key => target[key] !== undefined) ?? spec.key
    target[existingKey] = parseEditedValue(target[existingKey], text)
    return next
}

export function interviewCompileWithEditedFields(
    candidate: InterviewCompileCandidate,
    value: Record<string, unknown>,
    edits: Record<string, string>
): Record<string, unknown> {
    let next = JSON.parse(JSON.stringify(value)) as Record<string, unknown>
    for (const spec of editableSpecsFor(candidate)) {
        if (edits[spec.key] === undefined) continue
        if (!spec.required && !edits[spec.key].trim()) continue
        next = setField(next, spec, edits[spec.key])
    }
    return next
}

export function interviewCompileEditableCopy(
    candidate: InterviewCompileCandidate,
    value: Record<string, unknown> | undefined
): string {
    if (!value) return ''
    return interviewCompileEditableFields(candidate, value)[0]?.value ?? String(value.canonicalStatement ?? '')
}

export function interviewCompileWithEditedCopy(
    candidate: InterviewCompileCandidate,
    value: Record<string, unknown>,
    copy: string
): Record<string, unknown> {
    const primary = interviewCompileEditableFields(candidate, value)[0]
    return interviewCompileWithEditedFields(candidate, value, {
        [primary?.key ?? 'canonicalStatement']: copy.trim(),
    })
}
